import { useState, useEffect, useRef } from 'react';
import { Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ImageUpload = ({ value, onChange, token }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setPreview(value || '');
    setImageError(false);
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPEG, PNG and WebP images are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setImageError(false);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/admin/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = response.data.image_url;
      setPreview(imageUrl);
      onChange(imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
      console.error('Upload error:', error.response?.data || error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input 
          placeholder="Image URL (or upload below)" 
          value={value || ''} 
          onChange={(e) => {
            onChange(e.target.value);
            setPreview(e.target.value);
            setImageError(false);
          }}
          className="flex-1"
        />
        <button 
          type="button"
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
      {preview && !imageError && (
        <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        </div>
      )}
      {preview && imageError && (
        <div className="w-full bg-yellow-50 rounded-lg border border-yellow-200 p-4 space-y-3" data-testid="image-preview-broken">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-6 h-6 text-yellow-700 shrink-0" />
            <div className="flex-1 text-sm text-yellow-800">
              <p className="font-semibold">Image failed to load</p>
              <p className="text-xs text-yellow-700 mt-0.5">The URL above returns an error or is blocked. Upload a new image, paste a working URL, or clear it to fall back to the storefront placeholder.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { onChange(''); setPreview(''); setImageError(false); }}
              className="text-xs font-medium px-3 py-1.5 rounded-md border border-yellow-400 bg-white text-yellow-800 hover:bg-yellow-100"
              data-testid="image-preview-clear-broken"
            >
              Clear broken URL
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs font-medium px-3 py-1.5 rounded-md border border-[#FF6B35] bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
              data-testid="image-preview-upload-replacement"
            >
              Upload replacement
            </button>
          </div>
        </div>
      )}
      {!preview && (
        <div 
          className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="text-center text-gray-500">
            <ImageIcon className="w-8 h-8 mx-auto mb-1" />
            <span className="text-sm">Click to upload image</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick, testId }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  const wrapperProps = clickable
    ? {
        type: 'button',
        onClick,
        'data-testid': testId,
        className:
          'w-full text-left bg-white rounded-lg border p-4 transition-all cursor-pointer hover:border-[#0A2342] hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#0A2342]/40',
      }
    : {
        'data-testid': testId,
        className: 'bg-white rounded-lg border p-4',
      };

  return (
    <Wrapper {...wrapperProps}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-gray-600">{title}</span>
      </div>
      <p className="text-2xl font-bold text-[#0A2342]">{value}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </Wrapper>
  );
};

export const downloadPdfWithAuth = async (url, filename, token) => {
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('PDF download error:', error);
    toast.error('Failed to download PDF');
  }
};



// --- Supplier picker -------------------------------------------------------
// Lightweight cached fetch of suppliers + dropdown used in every product /
// course / event / trip edit form so admins can tie items to a vendor.

let _suppliersCache = null;
let _suppliersPromise = null;

export const fetchSuppliers = async (token) => {
  if (_suppliersCache) return _suppliersCache;
  if (_suppliersPromise) return _suppliersPromise;
  _suppliersPromise = axios
    .get(`${API_URL}/admin/suppliers`, { headers: { Authorization: `Bearer ${token}` } })
    .then((res) => {
      _suppliersCache = (res.data || []).filter((s) => s.status !== 'deleted');
      return _suppliersCache;
    })
    .catch(() => {
      _suppliersCache = [];
      return _suppliersCache;
    })
    .finally(() => { _suppliersPromise = null; });
  return _suppliersPromise;
};

export const refreshSuppliersCache = () => { _suppliersCache = null; };

export const SupplierPicker = ({ value, onChange, token, testId = 'supplier-picker' }) => {
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchSuppliers(token).then((list) => { if (alive) setSuppliers(list); });
    return () => { alive = false; };
  }, [token]);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Supplier (optional)</label>
      <select
        className="w-full border rounded-md px-3 py-2 text-sm"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testId}
      >
        <option value="">— No supplier —</option>
        {suppliers.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}{s.status !== 'active' ? ` (${s.status})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
};
